import React from "react";
import {
  TaxEventItemClass,
  formatAmount,
  getCurrencySymbol,
  useProfile,
  TaxType,
  CapitalTerm,
  useTaxEventsData,
} from "@moonscape/shared";
import { DocumentChartBarIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { CoinMark, DHead, Tag } from "@/components/ms";
import { FigLink, HeadLink } from "./chrome";

export const RecentTaxDisposalsWidget = () => {
  const { profile } = useProfile();
  const currentTaxYear = new Date().getFullYear() - 1; // filing for previous year

  // Fetch recent tax events (disposals)
  const { taxEvents, isQueryLoading } = useTaxEventsData({
    page: 0,
    taxYear: currentTaxYear,
    taxType: TaxType.CAPITAL_GAIN,
    capitalTerm: CapitalTerm.ALL,
    onLoading: () => {},
  });

  const events = taxEvents?.items || [];
  const recentEvents = events.slice(0, 5);
  const totalEvents = taxEvents?.total || 0;

  const currencySymbol = getCurrencySymbol(profile?.baseCurrency || "USD");

  // Loading state
  if (isQueryLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Recent disposals" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton circle width={30} height={30} />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </section>
    );
  }

  // Empty state
  if (recentEvents.length === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Recent disposals" />
        <EmptyWidget
          icon={<DocumentChartBarIcon className="h-8 w-8" />}
          title={`No tax disposals yet for ${currentTaxYear}`}
          description="Disposals appear here once your tax report is generated"
        />
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <DHead
        label={`Recent disposals · ${currentTaxYear}`}
        right={<HeadLink to="/tax/cgt">{totalEvents} total</HeadLink>}
      />

      <div className="flex flex-col">
        {recentEvents.map((event, index) => {
          const t = new TaxEventItemClass(event);
          return (
            <FigLink
              key={`${event.id || index}`}
              to="/tax/cgt"
              lead={<CoinMark sym={t?.asset} />}
              label={t.asset}
              sub={`${formatAmount(t.quantity)} sold · ${t.formattedTime()} · proceeds ${formatAmount(
                t.proceeds,
                currencySymbol,
                true
              )}`}
              tag={t.disposal_type ? <Tag tone="neu">{t.disposal_type}</Tag> : undefined}
              value={formatAmount(t.gain, currencySymbol, true)}
              tone={t.gain > 0 ? "green" : t.gain < 0 ? "neg" : "mute"}
            />
          );
        })}
      </div>
    </section>
  );
};
